import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Casino as SpinIcon,
  Whatshot as HotIcon,
  Campaign as AnnounceIcon,
  Groups as UsersIcon,
  PlayArrow as GoIcon,
  Timer as TimerIcon,
  Construction as BuildIcon,
  Flare as LuckyIcon,
  Stop as EndIcon,
  Refresh as RefreshIcon,
  Close as RemoveIcon,
  PersonOff as ExcludeIcon,
  PersonAdd as IncludeIcon,
  EmojiEvents as TrophyIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import io from 'socket.io-client';
import { useSnackbar } from 'notistack';
import LiveWheelDisplay from '../../Common/LiveWheelDisplay';
import DefaultStyleWheel, { prizeIndexFromSpin } from '../../Common/DefaultStyleWheel';
import {
  isPlaceholderSliceLabel,
  resolveMediaUrl,
  sliceHasArt,
} from '../../Common/ConfiguredWheelFace';
import { useSpinWheelHostSession } from './useSpinWheelHostSession';
import {
  SW_GAME_TYPES,
  SW_PHASES,
  isWheelVisiblePhase,
  isUserWheelPhase,
} from '../../UserSide/pages/spinHostPhases';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const getToken = () => localStorage.getItem('auth_token');

const getDisplayName = (entry) => {
  if (entry.display_name?.trim()) return entry.display_name.trim();
  if (entry.email) {
    const local = entry.email.split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1).replace(/[._]/g, ' ');
  }
  return `User ${entry.id || ''}`;
};

const getInitials = (name) =>
  (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/** Participant row — include/exclude, remove, color (same as Users Wheel panel). */
const EntryCard = ({ entry, sessionId, onUpdate, busy }) => {
  const [colorOpen, setColorOpen] = useState(false);
  const authHeader = { Authorization: `Bearer ${getToken()}` };

  const toggle = async () => {
    await fetch(
      `${API_BASE}/api/spinning-wheel/admin/live/session/${sessionId}/entries/${entry.id}/toggle`,
      { method: 'POST', headers: authHeader }
    );
    onUpdate();
  };

  const remove = async () => {
    await fetch(
      `${API_BASE}/api/spinning-wheel/admin/live/session/${sessionId}/entries/${entry.id}`,
      { method: 'DELETE', headers: authHeader }
    );
    onUpdate();
  };

  const changeColor = async (color) => {
    await fetch(
      `${API_BASE}/api/spinning-wheel/admin/live/session/${sessionId}/entries/${entry.id}/color`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ color }),
      }
    );
    setColorOpen(false);
    onUpdate();
  };

  const isExcluded = !!entry.is_excluded;
  const isWinner = !!entry.is_winner;
  const name = getDisplayName(entry);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.8,
        borderRadius: 1.5,
        mb: 0.5,
        opacity: isExcluded ? 0.45 : 1,
        bgcolor: isWinner
          ? 'rgba(255,215,0,0.12)'
          : isExcluded
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(255,255,255,0.06)',
        border: isWinner ? '1px solid rgba(255,215,0,0.4)' : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <Tooltip title="Change slice color">
        <Box
          onClick={() => setColorOpen((p) => !p)}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: entry.slice_color || '#FF6B6B',
            cursor: 'pointer',
            border: '2px solid rgba(255,255,255,0.3)',
            flexShrink: 0,
          }}
        />
      </Tooltip>

      {colorOpen && (
        <input
          type="color"
          defaultValue={entry.slice_color || '#FF6B6B'}
          style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
          onChange={(e) => changeColor(e.target.value)}
          onBlur={() => setColorOpen(false)}
          autoFocus
        />
      )}

      <Avatar
        src={entry.avatar_url || undefined}
        sx={{ width: 32, height: 32, fontSize: 12, bgcolor: entry.slice_color || '#555' }}
      >
        {getInitials(name)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: isWinner ? 700 : 400, color: isWinner ? '#FFD700' : 'inherit' }}
        >
          {isWinner && <TrophyIcon sx={{ fontSize: 14, mr: 0.4, verticalAlign: 'middle' }} />}
          {name}
        </Typography>
        {entry.email && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {entry.email}
          </Typography>
        )}
        {!entry.email && entry.current_page && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {entry.current_page}
          </Typography>
        )}
      </Box>

      <Chip
        label={entry.user_type === 'registered' ? 'user' : entry.user_type || 'anon'}
        size="small"
        sx={{ fontSize: 10, height: 18 }}
        color={entry.user_type === 'registered' ? 'primary' : 'default'}
      />

      <Tooltip title={isExcluded ? 'Include in spin' : 'Exclude from spin'}>
        <IconButton size="small" onClick={toggle} disabled={busy}>
          {isExcluded ? <IncludeIcon fontSize="small" /> : <ExcludeIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove from wheel">
        <IconButton size="small" color="error" onClick={remove} disabled={busy}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const PhaseChip = ({ phase }) => {
  const phaseLabels = {
    INVITE: '1. Invite',
    ANNOUNCE: '2. Announce',
    HOT_SEAT_SLIDE: '3. Hot Seat',
    LUCKY_PLAYER: '4. Lucky Player',
    BUILDING_USER_WHEEL: '5. Building Wheel',
    COUNTDOWN: '6. Countdown',
    USER_WHEEL_READY: '6b. User Wheel',
    WHEEL_READY: '7. Wheel Ready',
    SPINNING: 'Spinning…',
    RESULT: 'Result',
  };
  return (
    <Chip
      label={phaseLabels[phase] || phase || '—'}
      size="small"
      color="warning"
      sx={{ ml: 1, fontWeight: 700, letterSpacing: 0.04 }}
    />
  );
};

const StepIndicator = ({ step, total = 7, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: 'warning.main',
        color: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: '0.8rem',
      }}
    >
      {step}
    </Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Step {step} of {total}
    </Typography>
  </Box>
);

const PlayerSpinLockControl = ({ unlocked, busy, onLock, onUnlock }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
    <Chip
      size="small"
      icon={unlocked ? <LockOpenIcon /> : <LockIcon />}
      label={unlocked ? 'Unlocked — one spin' : 'Locked'}
      color={unlocked ? 'success' : 'default'}
      variant={unlocked ? 'filled' : 'outlined'}
    />
    {unlocked ? (
      <Button
        size="small"
        variant="outlined"
        startIcon={<LockIcon />}
        disabled={busy}
        onClick={onLock}
      >
        Lock
      </Button>
    ) : (
      <Button
        size="small"
        variant="contained"
        color="success"
        startIcon={<LockOpenIcon />}
        disabled={busy}
        onClick={onUnlock}
      >
        Unlock
      </Button>
    )}
  </Box>
);

const SpinWheelHotSeatTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    host,
    payload,
    loading: hostLoading,
    busy: hostBusy,
    error: hostError,
    setError: setHostError,
    startSession,
    endSession,
    // Enhanced flow
    announce,
    hotSeatSlide,
    luckyPlayer,
    buildUserWheel,
    countdown,
    spinUserWheel,
    startActualWheel,
    selectHotSeatManual,
    phase,
  } = useSpinWheelHostSession({ gameType: SW_GAME_TYPES.HOT_SEAT });

  const [prizeWheel, setPrizeWheel] = useState(null);
  const [wheels, setWheels] = useState([]);
  const [wheelsLoading, setWheelsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [selectedWheelId, setSelectedWheelId] = useState('');
  const [selectedWheelPreview, setSelectedWheelPreview] = useState(null);
  const [titleInput, setTitleInput] = useState('Hot Seat');
  const [previewUsers, setPreviewUsers] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [playerCanSpin, setPlayerCanSpin] = useState(false);
  const [playerSpinUnlocked, setPlayerSpinUnlocked] = useState(false);
  const [selectionMode, setSelectionMode] = useState('user_wheel');
  const [statusFilter, setStatusFilter] = useState('all');

  const [linkedSession, setLinkedSession] = useState(null);
  const linkedSessionRef = useRef(null);
  linkedSessionRef.current = linkedSession;
  const [spinning, setSpinning] = useState(false);
  const [finalAngle, setFinalAngle] = useState(0);
  const [spinDuration, setSpinDuration] = useState(6);
  const [winner, setWinner] = useState(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [prizeIndex, setPrizeIndex] = useState(0);

  // User wheel state (for selection spin) — full live session like Users Wheel panel
  const [userWheelSession, setUserWheelSession] = useState(null);
  const [userWheelEntries, setUserWheelEntries] = useState([]);
  const [uwSpinning, setUwSpinning] = useState(false);
  const [uwFinalAngle, setUwFinalAngle] = useState(0);
  const [uwSpinDuration, setUwSpinDuration] = useState(6);
  const [uwWinner, setUwWinner] = useState(null);
  const [uwAnimComplete, setUwAnimComplete] = useState(false);

  const spinningRef = useRef(false);
  const authHeader = { Authorization: `Bearer ${getToken()}` };
  const combinedBusy = busy || hostBusy;

  useEffect(() => {
    spinningRef.current = spinning || uwSpinning;
  }, [spinning, uwSpinning]);

  /* ─── Data loaders ─── */
  const loadWheels = useCallback(async () => {
    setWheelsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/wheels?includeInactive=true`, {
        headers: authHeader,
      });
      const data = await res.json();
      setWheels(data.wheels || []);
    } catch (e) {
      enqueueSnackbar('Failed to load wheels', { variant: 'error' });
    } finally {
      setWheelsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar]);

  const loadPreviewUsers = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetch(
        `${API_BASE}/api/spinning-wheel/admin/live/active-users${params}`,
        { headers: authHeader }
      );
      const data = await res.json();
      setPreviewUsers(data.users || []);
    } catch (e) {
      enqueueSnackbar('Failed to load active users', { variant: 'error' });
    } finally {
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, statusFilter]);

  const loadLinkedHotSeat = useCallback(async () => {
    if (!host?.linkedHotSeatSessionId || !isWheelVisiblePhase(phase) || isUserWheelPhase(phase)) {
      setLinkedSession(null);
      setPrizeWheel(null);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/hot-seat/session`, {
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success && data.session) {
        setLinkedSession(data.session);
        setPrizeWheel(data.prizeWheel || null);
        setPlayerCanSpin(!!data.session.player_can_spin);
        setPlayerSpinUnlocked(!!data.session.player_spin_unlocked);
        if (data.session.winner_slice_id && data.session.entries?.length) {
          const w = data.session.entries.find((e) => e.id === data.session.winner_slice_id);
          if (w) setWinner(w);
        }
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host?.linkedHotSeatSessionId, phase]);

  /** Load the linked live (users) session for hot-seat selection wheel management. */
  const loadUserWheelSession = useCallback(async () => {
    const managingUserWheel =
      phase === SW_PHASES.BUILDING_USER_WHEEL ||
      phase === SW_PHASES.COUNTDOWN ||
      phase === SW_PHASES.USER_WHEEL_READY;
    if (!host?.linkedLiveSessionId || !managingUserWheel) {
      if (!managingUserWheel) {
        setUserWheelSession(null);
        setUserWheelEntries([]);
      }
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/live/session`, {
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success && data.session) {
        setUserWheelSession(data.session);
        setUserWheelEntries(data.session.entries || []);
        if (data.session.winner_entry_id) {
          const w = (data.session.entries || []).find((e) => e.id === data.session.winner_entry_id);
          if (w) setUwWinner(w);
        }
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host?.linkedLiveSessionId, phase]);

  useEffect(() => { loadWheels(); loadPreviewUsers(); }, [loadWheels, loadPreviewUsers]);

  useEffect(() => {
    if (!selectedWheelId) {
      setSelectedWheelPreview(null);
      return undefined;
    }
    setSelectedWheelPreview(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/spinning-wheel/wheels/${selectedWheelId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (!cancelled && data.wheel) setSelectedWheelPreview(data.wheel);
      } catch {
        if (!cancelled) setSelectedWheelPreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWheelId]);
  useEffect(() => { loadLinkedHotSeat(); }, [loadLinkedHotSeat]);
  useEffect(() => { loadUserWheelSession(); }, [loadUserWheelSession]);

  // Sync host state
  useEffect(() => {
    if (host?.selectedHotSeat) {
      setSelectedUser((prev) =>
        prev || {
          device_id: host.selectedHotSeat.device_id,
          user_id: host.selectedHotSeat.user_id,
          display_name: host.selectedHotSeat.display_name,
        }
      );
    }
    if (host?.wheelId) setSelectedWheelId(String(host.wheelId));
    if (host?.playerCanSpin != null) setPlayerCanSpin(!!host.playerCanSpin);
    if (host?.selectionMethod) setSelectionMode(host.selectionMethod);
  }, [host]);

  // Sync user wheel entries from payload (public eligible list) when full session not loaded yet
  useEffect(() => {
    if (payload?.userWheelEntries?.length && !userWheelSession) {
      setUserWheelEntries(payload.userWheelEntries);
    }
  }, [payload, userWheelSession]);

  /* ─── Socket listeners ─── */
  const applyRemoteSpinStart = useCallback(({ finalAngle: angle, spinDuration: duration, winnerId, winnerIdx }) => {
    setWinner(null);
    setAnimationComplete(false);
    setFinalAngle(angle);
    setSpinDuration(duration);
    const list = linkedSessionRef.current?.entries || [];
    setPrizeIndex(prizeIndexFromSpin(winnerIdx, winnerId, list));
    if (winnerId) {
      const w = list.find((e) => e.id === winnerId);
      if (w) setTimeout(() => setWinner(w), 0);
    }
    setSpinning(true);
  }, []);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ['websocket'] });
    socket.on('connect', () => socket.emit('spinwheel:join-live'));

    socket.on('spinwheel:hot_seat_updated', ({ session: s, prizeWheel: pw }) => {
      if (s && Object.prototype.hasOwnProperty.call(s, 'player_spin_unlocked')) {
        setPlayerSpinUnlocked(!!s.player_spin_unlocked);
      }
      if (s && Object.prototype.hasOwnProperty.call(s, 'player_can_spin')) {
        setPlayerCanSpin(!!s.player_can_spin);
      }
      if (!spinningRef.current && s) {
        setLinkedSession(s);
        if (pw) setPrizeWheel(pw);
        if (s.winner_slice_id && s.entries?.length) {
          const w = s.entries.find((e) => e.id === s.winner_slice_id);
          if (w) setWinner(w);
        }
      }
    });

    socket.on('spinwheel:hot_seat_spin_start', (p) => applyRemoteSpinStart(p));

    socket.on('spinwheel:hot_seat_spin_complete', ({ session: s, winner: w }) => {
      if (s) {
        setLinkedSession(s);
        if (Object.prototype.hasOwnProperty.call(s, 'player_spin_unlocked')) {
          setPlayerSpinUnlocked(!!s.player_spin_unlocked);
        }
        if (Object.prototype.hasOwnProperty.call(s, 'player_can_spin')) {
          setPlayerCanSpin(!!s.player_can_spin);
        }
      }
      if (w) setWinner(w);
    });

    // User wheel spin events (for selection)
    socket.on('spinwheel:spin_start', ({ finalAngle: angle, spinDuration: dur, winnerId }) => {
      setUwWinner(null);
      setUwAnimComplete(false);
      setUwFinalAngle(angle);
      setUwSpinDuration(dur);
      setUwSpinning(true);
      if (winnerId) {
        setUserWheelEntries((prev) => {
          const w = prev.find((e) => e.id === winnerId);
          if (w) setTimeout(() => setUwWinner(w), 0);
          return prev;
        });
      }
    });

    socket.on('spinwheel:spin_complete', ({ winner: w, session: s }) => {
      if (w) setUwWinner(w);
      if (s) {
        setUserWheelSession(s);
        setUserWheelEntries(s.entries || []);
      }
    });

    socket.on('spinwheel:live_updated', (p) => {
      if (p?.session?.entries && !spinningRef.current) {
        setUserWheelSession(p.session);
        setUserWheelEntries(p.session.entries || []);
      }
    });

    return () => {
      socket.emit('spinwheel:leave-live');
      socket.disconnect();
    };
  }, [applyRemoteSpinStart]);

  /* ─── Handlers ─── */
  const postHotSeat = async (path, body = {}) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/hot-seat${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action failed');
      return data;
    } catch (e) {
      setError(e.message);
      enqueueSnackbar(e.message, { variant: 'error' });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, successMsg) => {
    try {
      await fn();
      if (successMsg) enqueueSnackbar(successMsg, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message || 'Action failed', { variant: 'error' });
    }
  };

  // BTN 1 — Start
  const handleStart = () =>
    run(
      () => startSession({ title: titleInput || 'Hot Seat' }),
      'Users see the Invite slide'
    );

  // BTN 2 — Announce
  const handleAnnounce = () => {
    if (!selectedWheelId) {
      enqueueSnackbar('Select a prize wheel first', { variant: 'warning' });
      return;
    }
    return run(
      () => announce({ wheel_id: parseInt(selectedWheelId, 10) }),
      'Wheel announced — users see the wheel details'
    );
  };

  // BTN 3 — Select Hot Seat
  const handleHotSeatSlide = () =>
    run(hotSeatSlide, 'Hot seat slide shown — "Who will it be?"');

  // BTN 4 — Lucky Player (user wheel path)
  const handleLuckyPlayer = () =>
    run(luckyPlayer, 'Lucky player slide shown — building user wheel next');

  // BTN 5 — Build User Wheel
  const handleBuildUserWheel = () =>
    run(async () => {
      await buildUserWheel({
        title: titleInput || 'Hot Seat Selection',
        status_filter: statusFilter,
      });
      await loadUserWheelSession();
    }, 'User wheel is being built — configure participants below');

  // Sync newly active users onto the selection wheel
  const handleSyncUserWheel = async () => {
    if (!userWheelSession?.id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/spinning-wheel/admin/live/session/${userWheelSession.id}/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({}),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Sync failed');
      if (data.session) {
        setUserWheelSession(data.session);
        setUserWheelEntries(data.session.entries || []);
      } else {
        await loadUserWheelSession();
      }
      enqueueSnackbar(
        `${data.added ?? 0} new participant(s) added`,
        { variant: 'success' }
      );
    } catch (e) {
      setError(e.message);
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // BTN 6 — Countdown
  const handleCountdown = () => {
    const eligibleUw = (userWheelEntries || []).filter((e) => !e.is_excluded);
    if (eligibleUw.length < 2) {
      enqueueSnackbar('Need at least 2 eligible participants before countdown', {
        variant: 'warning',
      });
      return;
    }
    return run(countdown, 'Countdown started — 3…2…1…');
  };

  // Spin User Wheel (during USER_WHEEL_READY)
  const handleSpinUserWheel = async () => {
    if (uwSpinning) return;
    setUwWinner(null);
    setUwAnimComplete(false);
    try {
      const data = await spinUserWheel();
      if (data) {
        setUwFinalAngle(data.finalAngle);
        setUwSpinDuration(data.spinDuration);
        setUwSpinning(true);
        if (data.winner) setUwWinner(data.winner);
      }
    } catch (e) {
      enqueueSnackbar(e.message || 'Spin failed', { variant: 'error' });
    }
  };

  // Manual selection — select user then BTN 7
  const handleManualSelect = () => {
    if (!selectedUser) {
      enqueueSnackbar('Select a hot seat player', { variant: 'warning' });
      return;
    }
    return run(
      () =>
        selectHotSeatManual({
          hot_seat_device_id: selectedUser.device_id || null,
          hot_seat_user_id: selectedUser.user_id || null,
          hot_seat_display_name: selectedUser.display_name || 'Player',
          player_can_spin: playerCanSpin,
        }),
      'Hot seat player selected manually'
    );
  };

  // BTN 7 — Start Actual Wheel
  const handleStartActualWheel = () => {
    const user = selectedUser || (host?.selectedHotSeat ? {
      device_id: host.selectedHotSeat.device_id,
      user_id: host.selectedHotSeat.user_id,
      display_name: host.selectedHotSeat.display_name,
    } : null);
    if (!user?.display_name) {
      enqueueSnackbar('Select a hot seat player first', { variant: 'warning' });
      return;
    }
    return run(
      () =>
        startActualWheel({
          hot_seat_device_id: user.device_id || null,
          hot_seat_user_id: user.user_id || null,
          hot_seat_display_name: user.display_name || 'Player',
          player_can_spin: playerCanSpin,
        }),
      'Prize wheel is live!'
    );
  };

  // Spin the prize wheel (hot seat wheel)
  const handleSpin = async () => {
    if (!linkedSession || spinning) return;
    setWinner(null);
    setAnimationComplete(false);
    const data = await postHotSeat(`/session/${linkedSession.id}/spin`);
    if (!data) return;
    setPrizeIndex(prizeIndexFromSpin(data.winnerIdx, data.winner?.id, linkedSession?.entries || []));
    setFinalAngle(data.finalAngle);
    setSpinDuration(data.spinDuration);
    setSpinning(true);
    if (data.winner) setWinner(data.winner);
  };

  const handleAnimationEnd = useCallback(() => {
    setSpinning(false);
    setAnimationComplete(true);
    loadLinkedHotSeat();
  }, [loadLinkedHotSeat]);

  const handleUwAnimEnd = useCallback(() => {
    setUwSpinning(false);
    setUwAnimComplete(true);
    loadUserWheelSession();
  }, [loadUserWheelSession]);

  const patchPlayerCanSpin = async (nextPlayerCanSpin) => {
    if (!linkedSession) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/spinning-wheel/admin/hot-seat/session/${linkedSession.id}/settings`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({ player_can_spin: !!nextPlayerCanSpin }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update settings');
      setPlayerCanSpin(!!nextPlayerCanSpin);
      setPlayerSpinUnlocked(!!data.session?.player_spin_unlocked);
      if (data.session) setLinkedSession(data.session);
      if (data.prizeWheel) setPrizeWheel(data.prizeWheel);
      enqueueSnackbar(
        nextPlayerCanSpin
          ? 'Player spin is on and locked — unlock it when you want one spin'
          : 'Host spin is on — you spin from this panel',
        { variant: 'success' }
      );
    } catch (e) {
      setError(e.message);
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const patchPlayerSpinLock = async (unlocked) => {
    if (!linkedSession || !playerCanSpin) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/spinning-wheel/admin/hot-seat/session/${linkedSession.id}/settings`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({
            player_can_spin: true,
            player_spin_unlocked: !!unlocked,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update spin lock');
      setPlayerSpinUnlocked(!!data.session?.player_spin_unlocked);
      if (data.session) setLinkedSession(data.session);
      if (data.prizeWheel) setPrizeWheel(data.prizeWheel);
      enqueueSnackbar(
        unlocked
          ? 'Unlocked — the hot seat player can spin once. It locks again after that spin.'
          : 'Locked — the player cannot spin until you unlock again',
        { variant: 'success' }
      );
    } catch (e) {
      setError(e.message);
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = async () => {
    try {
      await endSession();
    } catch (e) {
      enqueueSnackbar(e.message || 'Failed to end', { variant: 'error' });
    }
    // Reset all local state back to initial (BTN 1 "Start")
    setWinner(null);
    setSpinning(false);
    setAnimationComplete(false);
    setLinkedSession(null);
    setPrizeWheel(null);
    setUwWinner(null);
    setUwSpinning(false);
    setUwAnimComplete(false);
    setUserWheelEntries([]);
    setUserWheelSession(null);
    setSelectedUser(null);
    setSelectedWheelId('');
    setSelectedWheelPreview(null);
    setSelectionMode('user_wheel');
    setPlayerCanSpin(false);
    setPlayerSpinUnlocked(false);
    setBusy(false);
    setError(null);
  };

  /* ─── Derived state ─── */
  const entries = linkedSession?.entries || [];
  const eligible = entries.filter((e) => !e.is_excluded);
  const uwEntries = userWheelEntries || [];
  const uwEligible = uwEntries.filter((e) => !e.is_excluded);
  const uwExcluded = uwEntries.filter((e) => e.is_excluded);
  const uwWinners = uwEntries.filter((e) => e.is_winner);
  const showUserWheelManager =
    host &&
    (phase === SW_PHASES.BUILDING_USER_WHEEL ||
      phase === SW_PHASES.COUNTDOWN ||
      phase === SW_PHASES.USER_WHEEL_READY) &&
    !!userWheelSession;
  const hotSeatName =
    host?.selectedHotSeat?.display_name ||
    linkedSession?.hot_seat_display_name ||
    selectedUser?.display_name ||
    '';
  const centerLogo = '/mind_your_music.png';
  const wheelSliceCount = (wheel) => {
    if (!wheel) return 0;
    if (wheel.slice_count != null && wheel.slice_count !== '') return Number(wheel.slice_count);
    if (Array.isArray(wheel.slices)) return wheel.slices.length;
    return 0;
  };
  const selectedListWheel = wheels.find((w) => String(w.wheel_id) === String(selectedWheelId));
  const selectedSliceTotal = selectedWheelPreview
    ? (selectedWheelPreview.slices || []).length
    : wheelSliceCount(selectedListWheel);
  const showPrizeWheel = host && isWheelVisiblePhase(phase) && !isUserWheelPhase(phase);
  const showUserWheel = host && isUserWheelPhase(phase);
  const displayError = error || hostError;

  if (hostLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
        <HotIcon sx={{ verticalAlign: 'middle', mr: 0.5, color: '#ff6b35' }} />
        Hot Seat Mode — Step-by-Step
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Follow the 7-button flow: Start → Announce → Select Hot Seat → (User Wheel or Manual)
        → Start the Actual Wheel. Each button shows a slide on user screens + plays a sound.
      </Typography>

      {displayError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => { setError(null); setHostError(null); }}
        >
          {displayError}
        </Alert>
      )}

      {/* ══════ NO SESSION — BTN 1: START ══════ */}
      {!host && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
          <StepIndicator step={1} label="Start — Invite People" />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Session title"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="warning"
                size="large"
                startIcon={<GoIcon />}
                disabled={combinedBusy}
                onClick={handleStart}
                sx={{ fontWeight: 700, px: 4 }}
              >
                BTN 1 — Start
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Shows the &ldquo;Invite people to Spin the Wheel&rdquo; slide with info about
            the Users Wheel &amp; Hot Seat wheel.
          </Typography>
        </Paper>
      )}

      {/* ══════ ACTIVE SESSION — FLOW PANEL ══════ */}
      {host && (
        <>
          {/* Header bar */}
          <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#1E1E1E' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                {host.title}
                <PhaseChip phase={phase} />
                {hotSeatName && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Hot seat: <strong>{hotSeatName}</strong>
                  </Typography>
                )}
              </Typography>
              {showPrizeWheel && linkedSession && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={playerCanSpin}
                      disabled={combinedBusy || spinning}
                      onChange={(e) => patchPlayerCanSpin(e.target.checked)}
                      color="warning"
                      size="small"
                    />
                  }
                  label="Player spins"
                  sx={{ mr: 1 }}
                />
              )}
              {showPrizeWheel && linkedSession && playerCanSpin && (
                <PlayerSpinLockControl
                  unlocked={playerSpinUnlocked}
                  busy={combinedBusy || spinning}
                  onLock={() => patchPlayerSpinLock(false)}
                  onUnlock={() => patchPlayerSpinLock(true)}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<EndIcon />}
                disabled={combinedBusy || spinning || uwSpinning}
                onClick={handleEnd}
              >
                End session
              </Button>
            </Box>
          </Paper>

          {/* ── BTN 2: ANNOUNCE (visible during INVITE phase) ── */}
          {phase === SW_PHASES.INVITE && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
              <StepIndicator step={2} label="Announce — Select & Announce the Wheel" />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" disabled={wheelsLoading}>
                    <InputLabel>Prize wheel to play</InputLabel>
                    <Select
                      label="Prize wheel to play"
                      value={selectedWheelId}
                      onChange={(e) => setSelectedWheelId(e.target.value)}
                    >
                      {wheels.map((w) => {
                        const total = wheelSliceCount(w);
                        return (
                          <MenuItem key={w.wheel_id} value={String(w.wheel_id)}>
                            {w.wheel_name} ({total} {total === 1 ? 'slice' : 'slices'})
                            {!w.is_active ? ' (inactive)' : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {selectedWheelId && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      {selectedWheelPreview ? (
                        <DefaultStyleWheel
                          slices={selectedWheelPreview.slices || []}
                          logoUrl="/mind_your_music.png"
                          maxSize={168}
                        />
                      ) : (
                        <CircularProgress size={28} />
                      )}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {selectedWheelPreview?.wheel_name || selectedListWheel?.wheel_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedSliceTotal} {selectedSliceTotal === 1 ? 'slice' : 'slices'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<AnnounceIcon />}
                    disabled={combinedBusy || !selectedWheelId}
                    onClick={handleAnnounce}
                    sx={{ fontWeight: 700, px: 4 }}
                  >
                    BTN 2 — Announce
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Shows the wheel name, description, slices and rules on user screens.
              </Typography>
            </Paper>
          )}

          {/* ── BTN 3: SELECT HOT SEAT (visible during ANNOUNCE) ── */}
          {phase === SW_PHASES.ANNOUNCE && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
              <StepIndicator step={3} label="Select Hot Seat" />
              <Button
                variant="contained"
                color="warning"
                size="large"
                startIcon={<HotIcon />}
                disabled={combinedBusy}
                onClick={handleHotSeatSlide}
                sx={{ fontWeight: 700, px: 4 }}
              >
                BTN 3 — Select Hot Seat
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Shows the Hot Seat slide: &ldquo;You could be the lucky player…&rdquo;
              </Typography>
            </Paper>
          )}

          {/* ── HOT_SEAT_SLIDE phase — choose manual or user wheel ── */}
          {phase === SW_PHASES.HOT_SEAT_SLIDE && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
              <StepIndicator step={3} label="Select Hot Seat — Choose Method" total={7} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How would you like to select the hot seat player?
              </Typography>
              <Grid container spacing={2}>
                {/* Option A: User Wheel */}
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: selectionMode === 'user_wheel' ? 'rgba(255,45,138,0.08)' : '#252525',
                      border: selectionMode === 'user_wheel'
                        ? '2px solid rgba(255,45,138,0.4)'
                        : '2px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'rgba(255,45,138,0.3)' },
                    }}
                    onClick={() => setSelectionMode('user_wheel')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <UsersIcon sx={{ color: '#ff2d8a' }} />
                      <Typography sx={{ fontWeight: 700 }}>Spin User Wheel</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Build a wheel from active users and spin it to randomly select the hot seat player.
                    </Typography>
                    {selectionMode === 'user_wheel' && (
                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #ff2d8a, #ff6b35)',
                        }}
                        startIcon={<LuckyIcon />}
                        disabled={combinedBusy}
                        onClick={handleLuckyPlayer}
                      >
                        BTN 4 — Lucky Player
                      </Button>
                    )}
                  </Paper>
                </Grid>

                {/* Option B: Manual */}
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: selectionMode === 'manual' ? 'rgba(255,193,7,0.08)' : '#252525',
                      border: selectionMode === 'manual'
                        ? '2px solid rgba(255,193,7,0.4)'
                        : '2px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'rgba(255,193,7,0.3)' },
                    }}
                    onClick={() => setSelectionMode('manual')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <HotIcon sx={{ color: '#ffc107' }} />
                      <Typography sx={{ fontWeight: 700 }}>Select Manually</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Pick the hot seat player from a dropdown of active users.
                    </Typography>
                    {selectionMode === 'manual' && (
                      <Box sx={{ mt: 2 }}>
                        <Autocomplete
                          size="small"
                          options={previewUsers}
                          loading={previewLoading}
                          getOptionLabel={(o) => o.display_name || 'User'}
                          value={selectedUser}
                          onChange={(_, v) => setSelectedUser(v)}
                          renderInput={(params) => (
                            <TextField {...params} label="Hot seat player" placeholder="Search active users" />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.device_id || option.session_id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 11 }}>
                                  {getInitials(option.display_name)}
                                </Avatar>
                                {option.display_name}
                              </Box>
                            </li>
                          )}
                        />
                        <Button
                          size="small"
                          sx={{ mt: 0.5 }}
                          onClick={loadPreviewUsers}
                          disabled={previewLoading}
                        >
                          Refresh users
                        </Button>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            color="warning"
                            disabled={combinedBusy || !selectedUser}
                            onClick={async () => {
                              await handleManualSelect();
                              handleStartActualWheel();
                            }}
                            sx={{ fontWeight: 700 }}
                          >
                            Select &amp; Start Wheel (BTN 7)
                          </Button>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={playerCanSpin}
                                onChange={(e) => setPlayerCanSpin(e.target.checked)}
                                color="warning"
                                size="small"
                              />
                            }
                            label="Player spins"
                          />
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* ── BTN 5: BUILD USER WHEEL (visible during LUCKY_PLAYER) ── */}
          {phase === SW_PHASES.LUCKY_PLAYER && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
              <StepIndicator step={5} label="Build User Wheel" />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {['all', 'active', 'idle', 'away'].map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    clickable
                    size="small"
                    variant={statusFilter === s ? 'filled' : 'outlined'}
                    color={statusFilter === s ? 'primary' : 'default'}
                    onClick={() => setStatusFilter(s)}
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {previewLoading ? 'Loading…' : `${previewUsers.length} active user(s) available`}
                <Button size="small" sx={{ ml: 1 }} onClick={loadPreviewUsers} disabled={previewLoading}>
                  Refresh list
                </Button>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, maxHeight: 80, overflow: 'auto' }}>
                {previewUsers.map((u, i) => (
                  <Chip
                    key={u.device_id || u.session_id || i}
                    label={u.display_name}
                    size="small"
                    sx={{ fontSize: 11 }}
                    variant="outlined"
                  />
                ))}
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<BuildIcon />}
                disabled={combinedBusy}
                onClick={handleBuildUserWheel}
                sx={{ fontWeight: 700, px: 4 }}
              >
                BTN 5 — Build User Wheel
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Builds the selection wheel from active users. Next: sync / include / exclude, then countdown &amp; spin.
              </Typography>
            </Paper>
          )}

          {/* ── User wheel manager (BUILDING / COUNTDOWN / READY) — full Users Wheel settings ── */}
          {showUserWheelManager && (
            <>
              <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#1E1E1E' }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                    Selection wheel
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {uwEligible.length} eligible · {uwEntries.length} total
                      {userWheelSession?.spin_count != null
                        ? ` · spin #${userWheelSession.spin_count}`
                        : ''}
                    </Typography>
                  </Typography>
                  <Tooltip title="Add newly active users to the wheel">
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={handleSyncUserWheel}
                      disabled={combinedBusy || uwSpinning}
                    >
                      Sync users
                    </Button>
                  </Tooltip>
                  <Button
                    size="small"
                    startIcon={<BuildIcon />}
                    onClick={handleBuildUserWheel}
                    disabled={combinedBusy || uwSpinning}
                  >
                    Rebuild
                  </Button>
                  {phase === SW_PHASES.BUILDING_USER_WHEEL && (
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      startIcon={<TimerIcon />}
                      disabled={combinedBusy || uwEligible.length < 2}
                      onClick={handleCountdown}
                      sx={{ fontWeight: 700 }}
                    >
                      BTN 6 — Countdown 3…2…1
                    </Button>
                  )}
                </Box>
                {phase === SW_PHASES.BUILDING_USER_WHEEL && (
                  <Alert severity="info" sx={{ mb: 0 }}>
                    Users see &quot;Building The Users Wheel…&quot;. Sync / include / exclude below, then hit{' '}
                    <strong>Countdown</strong> when ready to reveal &amp; spin.
                  </Alert>
                )}
                {phase === SW_PHASES.COUNTDOWN && (
                  <Alert severity="info">
                    Countdown is playing on user screens. The user wheel appears automatically after 3…2…1.
                  </Alert>
                )}
              </Paper>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, bgcolor: '#1E1E1E', textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="info.main" sx={{ mb: 1, fontWeight: 700 }}>
                      {showUserWheel
                        ? 'User Wheel — Select Hot Seat Player'
                        : 'Wheel preview (hidden from users until countdown)'}
                    </Typography>
                    {showUserWheel || phase === SW_PHASES.BUILDING_USER_WHEEL ? (
                      <>
                        <LiveWheelDisplay
                          entries={uwEntries}
                          spinning={uwSpinning}
                          finalAngle={uwFinalAngle}
                          spinDuration={uwSpinDuration}
                          winnerEntry={uwWinner}
                          animationComplete={uwAnimComplete}
                          onAnimationEnd={handleUwAnimEnd}
                          size={340}
                        />
                        <Box sx={{ mt: 2 }}>
                          {showUserWheel && !uwWinner && (
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={
                                uwSpinning ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <SpinIcon />
                                )
                              }
                              disabled={combinedBusy || uwSpinning || uwEligible.length < 2}
                              onClick={handleSpinUserWheel}
                              sx={{
                                background: uwSpinning
                                  ? undefined
                                  : 'linear-gradient(135deg,#ff2d8a,#FFD700)',
                                fontWeight: 700,
                                px: 5,
                                py: 1.2,
                              }}
                            >
                              {uwSpinning ? 'Spinning…' : 'SPIN User Wheel'}
                            </Button>
                          )}
                          {uwEligible.length < 2 && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              display="block"
                              sx={{ mt: 1 }}
                            >
                              Need at least 2 eligible participants
                            </Typography>
                          )}
                          {uwWinner && uwAnimComplete && (
                            <Alert severity="success" sx={{ mt: 1, mb: 1, textAlign: 'left' }}>
                              Hot seat player: <strong>{getDisplayName(uwWinner)}</strong>
                            </Alert>
                          )}
                        </Box>
                      </>
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 8 }}>
                        Waiting for countdown…
                        <br />
                        {uwEligible.length} eligible participant(s) ready.
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: '#1E1E1E',
                      maxHeight: 560,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Participants ({uwEntries.length})
                    </Typography>
                    <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                      {uwWinners.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFD700' }}>
                            WINNERS ({uwWinners.length})
                          </Typography>
                          {uwWinners.map((e) => (
                            <EntryCard
                              key={e.id}
                              entry={e}
                              sessionId={userWheelSession.id}
                              onUpdate={loadUserWheelSession}
                              busy={combinedBusy || uwSpinning}
                            />
                          ))}
                          <Divider sx={{ my: 1 }} />
                        </>
                      )}
                      {uwEligible.filter((e) => !e.is_winner).length > 0 && (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            ELIGIBLE ({uwEligible.filter((e) => !e.is_winner).length})
                          </Typography>
                          {uwEligible
                            .filter((e) => !e.is_winner)
                            .map((e) => (
                              <EntryCard
                                key={e.id}
                                entry={e}
                                sessionId={userWheelSession.id}
                                onUpdate={loadUserWheelSession}
                                busy={combinedBusy || uwSpinning}
                              />
                            ))}
                        </>
                      )}
                      {uwExcluded.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            EXCLUDED ({uwExcluded.length})
                          </Typography>
                          {uwExcluded.map((e) => (
                            <EntryCard
                              key={e.id}
                              entry={e}
                              sessionId={userWheelSession.id}
                              onUpdate={loadUserWheelSession}
                              busy={combinedBusy || uwSpinning}
                            />
                          ))}
                        </>
                      )}
                      {uwEntries.length === 0 && (
                        <Alert severity="info">
                          No participants yet. Hit Sync users or Rebuild.
                        </Alert>
                      )}
                    </Box>

                    {showUserWheel && uwWinner && uwAnimComplete && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <StepIndicator step={7} label="Start the Actual Wheel" />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={playerCanSpin}
                              onChange={(e) => setPlayerCanSpin(e.target.checked)}
                              color="warning"
                              size="small"
                            />
                          }
                          label="Player spins"
                          sx={{ mb: 1, display: 'block' }}
                        />
                        <Button
                          variant="contained"
                          color="success"
                          size="large"
                          startIcon={<GoIcon />}
                          disabled={combinedBusy}
                          onClick={() => {
                            setSelectedUser({
                              device_id: uwWinner.device_id || null,
                              user_id: uwWinner.user_id || null,
                              display_name: getDisplayName(uwWinner),
                            });
                            return run(
                              () =>
                                startActualWheel({
                                  hot_seat_device_id: uwWinner.device_id || null,
                                  hot_seat_user_id: uwWinner.user_id || null,
                                  hot_seat_display_name: getDisplayName(uwWinner),
                                  player_can_spin: playerCanSpin,
                                }),
                              'Prize wheel is live!'
                            );
                          }}
                          sx={{ fontWeight: 700, px: 4 }}
                        >
                          BTN 7 — Start Actual Wheel
                        </Button>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          Playing for <strong>{getDisplayName(uwWinner)}</strong>
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {/* Fallback: BUILDING without session loaded yet */}
          {phase === SW_PHASES.BUILDING_USER_WHEEL && !userWheelSession && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1E1E1E' }}>
              <CircularProgress size={22} sx={{ mr: 1, verticalAlign: 'middle' }} />
              <Typography component="span" variant="body2" color="text.secondary">
                Loading user wheel participants…
              </Typography>
              <Button size="small" sx={{ ml: 1 }} onClick={loadUserWheelSession}>
                Retry
              </Button>
            </Paper>
          )}

          {/* ── PRIZE WHEEL (WHEEL_READY / SPINNING / RESULT) ── */}
          {showPrizeWheel && linkedSession && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: '#1E1E1E', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, fontWeight: 700 }}>
                    Playing for: {hotSeatName}
                  </Typography>
                  <LiveWheelDisplay
                    entries={entries}
                    spinning={spinning}
                    finalAngle={finalAngle}
                    spinDuration={spinDuration}
                    winnerEntry={winner}
                    animationComplete={animationComplete}
                    onAnimationEnd={handleAnimationEnd}
                    size={Math.min(380, 445)}
                    centerLogoSrc={centerLogo}
                    hotSeatPlayerName={hotSeatName}
                    backgroundImageUrl={prizeWheel?.background_image_url || ''}
                    variant="default"
                    prizeIndex={prizeIndex}
                  />
                  <Box sx={{ mt: 3 }}>
                    {playerCanSpin ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
                        <Typography variant="body2" color="text.secondary">
                          Player spin is on for <strong>{hotSeatName}</strong>. Unlock gives them one spin, then it locks again.
                        </Typography>
                        <PlayerSpinLockControl
                          unlocked={playerSpinUnlocked}
                          busy={combinedBusy || spinning}
                          onLock={() => patchPlayerSpinLock(false)}
                          onUnlock={() => patchPlayerSpinLock(true)}
                        />
                      </Box>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={
                            spinning ? <CircularProgress size={18} color="inherit" /> : <SpinIcon />
                          }
                          disabled={combinedBusy || spinning || eligible.length < 2}
                          onClick={handleSpin}
                          sx={{
                            background: spinning ? undefined : 'linear-gradient(135deg,#ff6b35,#FFD700)',
                            fontWeight: 700,
                            px: 5,
                            py: 1.2,
                          }}
                        >
                          {spinning ? 'Spinning…' : 'SPIN for hot seat'}
                        </Button>
                        {eligible.length < 2 && !spinning && (
                          <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                            Wheel needs at least 2 slices
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#1E1E1E' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Prize slices ({eligible.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {eligible.map((e) => {
                      const art = sliceHasArt(e);
                      const graphicWheel = !!prizeWheel?.background_image_url && !eligible.some(sliceHasArt);
                      const hidePlaceholder =
                        isPlaceholderSliceLabel(e.display_name) && (art || graphicWheel);
                      const artUrl = resolveMediaUrl(e.background_image_url || e.image_url);
                      return (
                        <Chip
                          key={e.id}
                          avatar={artUrl ? <Avatar src={artUrl} alt="" /> : undefined}
                          label={hidePlaceholder ? (e.points > 0 ? `+${e.points}` : 'Prize') : (e.display_name || 'Prize')}
                          size="small"
                          sx={{
                            bgcolor: e.slice_color,
                            color: '#fff',
                            fontWeight: e.is_winner ? 800 : 400,
                            border: e.is_winner ? '2px solid #FFD700' : 'none',
                            '& .MuiChip-avatar': { width: 18, height: 18 },
                          }}
                        />
                      );
                    })}
                  </Box>
                  {winner && animationComplete && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Result for <strong>{hotSeatName}</strong>
                      {!(
                        isPlaceholderSliceLabel(winner.display_name) &&
                        (sliceHasArt(winner) || prizeWheel?.background_image_url)
                      ) && (
                        <>
                          : <strong>{winner.display_name}</strong>
                        </>
                      )}
                      {winner.points > 0 ? ` (+${winner.points} pts)` : ''}
                    </Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* ── COUNTDOWN phase info ── */}
          {phase === SW_PHASES.COUNTDOWN && (
            <Alert severity="info">
              Countdown is playing on user screens. The user wheel will appear automatically after 3…2…1.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default SpinWheelHotSeatTab;
